import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, KeyRound } from "lucide-react";

/**
 * ChangePasswordModal — forces the user to change their password if `must_change_password` is set.
 * Also usable as a normal "Change my password" dialog when opened voluntarily via `openForced=false`.
 */
export default function ChangePasswordModal({ open, onClose, forced = false }) {
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (pw1.length < 8) { toast.error("Yeni şifre en az 8 karakter olmalı"); return; }
    if (pw1 !== pw2) { toast.error("Şifreler eşleşmiyor"); return; }
    if (!forced && !current) { toast.error("Mevcut şifreyi girin"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: forced ? undefined : current,
        new_password: pw1,
      });
      toast.success("Şifre başarıyla değiştirildi");
      // Clear must_change_password locally
      if (user) setUser({ ...user, must_change_password: false });
      setCurrent(""); setPw1(""); setPw2("");
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Şifre değiştirilemedi");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!forced) onClose?.(); return v; }}>
      <DialogContent className="max-w-md" onEscapeKeyDown={(e) => { if (forced) e.preventDefault(); }} onPointerDownOutside={(e) => { if (forced) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {forced ? <ShieldAlert className="w-5 h-5 text-red-600" /> : <KeyRound className="w-5 h-5" />}
            {forced ? "Güvenlik: Şifre Değiştirin" : "Şifremi Değiştir"}
          </DialogTitle>
        </DialogHeader>
        {forced && (
          <div className="p-3 rounded border-l-4 border-red-500 bg-red-50 dark:bg-red-950/30 text-xs">
            <p className="font-semibold text-red-700 dark:text-red-400">Devam etmek için şifrenizi güncellemeniz gerekir.</p>
            <p className="text-red-800 dark:text-red-300 mt-1">Bu, hesabınızın güvenliğini sağlamak için zorunlu bir adımdır. Varsayılan veya paylaşılan şifreler artık kabul edilmemektedir.</p>
          </div>
        )}
        <div className="space-y-3">
          {!forced && (
            <div>
              <Label>Mevcut Şifre</Label>
              <Input type="password" value={current} onChange={e => setCurrent(e.target.value)} data-testid="cp-current" autoComplete="current-password" />
            </div>
          )}
          <div>
            <Label>Yeni Şifre (en az 8 karakter)</Label>
            <Input type="password" value={pw1} onChange={e => setPw1(e.target.value)} data-testid="cp-new1" autoComplete="new-password" />
          </div>
          <div>
            <Label>Yeni Şifre (Tekrar)</Label>
            <Input type="password" value={pw2} onChange={e => setPw2(e.target.value)} data-testid="cp-new2" autoComplete="new-password" />
          </div>
          <p className="text-[11px] text-muted-foreground">
            💡 Güçlü şifre önerisi: en az 12 karakter, büyük/küçük harf, sayı ve özel karakter içermelidir.
          </p>
        </div>
        <DialogFooter>
          {!forced && <Button variant="outline" onClick={onClose}>İptal</Button>}
          <Button onClick={submit} disabled={loading} data-testid="cp-submit">
            {loading ? "Kaydediliyor..." : (forced ? "Şifreyi Belirle & Devam Et" : "Kaydet")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
